import * as React from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Box, Card, Grid, CardContent, CardMedia, Typography, Button, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';

import DialogMoreInfoActivity from '../components/activities/DialogMoreInfoActivity';
import { obtenerActividades, obtenerDatosHotel } from '../services/motorService';

// ✅ El itinerario (HotProdItinerario) viene como HTML (<p>, <div>, etc).
// Lo limpiamos para poder mostrarlo como texto plano, conservando saltos de
// línea donde había párrafos/<br> para que se lea ordenado.
const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();
};

const truncate = (text, maxLength = 160) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}…` : text;
};

// ✅ El formato real (png/jpeg/etc) puede variar según lo que suba cada hotel,
// así que lo detectamos mirando el "magic number" de los primeros bytes del
// base64, en vez de asumir uno fijo (asumir mal el mime puede hacer que el
// navegador no decodifique bien la imagen).
const detectImageMime = (base64) => {
  if (base64.startsWith('iVBORw0KGgo')) return 'image/png';
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('R0lGODlh') || base64.startsWith('R0lGODdh')) return 'image/gif';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg'; // fallback razonable si no matchea ninguna firma conocida
};

// ✅ HotProdFoto viene en base64 "pelado" (sin el prefijo "data:image/...;base64,"),
// así que lo armamos acá para poder usarlo directo en un <img>/CardMedia. Si en
// algún caso ya viniera con el prefijo puesto, lo dejamos tal cual.
const buildImageSrc = (base64) => {
  if (!base64) return null;
  if (/^data:image\//i.test(base64)) return base64;
  const clean = base64.replace(/\s/g, '');
  return `data:${detectImageMime(clean)};base64,${clean}`;
};

// 🐞 DEBUG: un base64 válido tiene longitud múltiplo de 4 y solo usa
// caracteres A-Z/a-z/0-9/+//, con "=" solo de relleno al final. Si esto da
// false, el string vino cortado o corrupto desde la API (no es un problema
// de resolución/calidad de la foto, es que el dato en sí llegó incompleto).
const isValidBase64 = (base64) => {
  if (!base64) return false;
  const clean = base64.replace(/\s/g, '');
  if (clean.length === 0 || clean.length % 4 !== 0) return false;
  return /^[A-Za-z0-9+/]*={0,2}$/.test(clean);
};

function Activities() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // ⚠️ Este es el código de hotel del MOTOR (viene por la URL/cookie). Las
  // APIs de actividades (Devuelvo_Actividades / Actividad_Detalle) NO usan
  // este código: usan el código de hotel de ADMINTOUR, que se obtiene abajo
  // llamando a Muestro_DatosHotel (campo "MotorHotAdmintour").
  const hotCod = params.get('hotcod') ?? Cookies.get('hotCod') ?? null;

  // ✅ Código de hotel de ADMINTOUR (MotorHotAdmintour), el que hay que
  // pasarle a las APIs de actividades. Se resuelve una vez por hotCod.
  const [hotCodAdmintour, setHotCodAdmintour] = React.useState(null);

  const [activitiesList, setActivitiesList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [errorMsg, setErrorMsg] = React.useState(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedActivity, setSelectedActivity] = React.useState(null);

  // ✅ Actividades cuya imagen falló al cargar (id -> true). Para esas
  // mostramos el placeholder aunque la API haya mandado un HotProdFoto.
  const [imageLoadErrors, setImageLoadErrors] = React.useState({});

  const handleImageError = (activityId) => {
    setImageLoadErrors((prev) => ({ ...prev, [activityId]: true }));
  };

  React.useEffect(() => {
    let isMounted = true;

    const fetchActivities = async () => {
      if (!hotCod) {
        setLoading(false);
        setErrorMsg('No se pudo determinar el hotel para buscar actividades.');
        return;
      }

      try {
        setLoading(true);
        setErrorMsg(null);

        // ✅ Primero resolvemos el código de hotel de Admintour a partir del
        // código de hotel del motor, vía Muestro_DatosHotel. Las APIs de
        // actividades necesitan el de Admintour, no el del motor.
        const datosHotel = await obtenerDatosHotel(hotCod);
        const hotCodAdmin = datosHotel?.MotorHotAdmintour;

        if (!hotCodAdmin) {
          if (isMounted) {
            setErrorMsg('No se pudo determinar el código de hotel de Admintour para buscar actividades.');
          }
          return;
        }

        if (isMounted) setHotCodAdmintour(hotCodAdmin);

        const data = await obtenerActividades(hotCodAdmin);
        const coleccion = data?.ColeccionActividades || [];

        // 🐞 DEBUG: para cada actividad, chequeamos si el base64 de la foto
        // llegó completo/bien formado. Si ves "válido: false" en alguna, el
        // problema es que la API está mandando ese campo cortado o corrupto,
        // no algo que se arregle desde acá.
        coleccion.forEach((item) => {
          const foto = item.HotProdFoto || '';
          console.log('[Activities] HotProdFoto', item.HotProdNomLargo, {
            largo: foto.length,
            valido: isValidBase64(foto),
            primeros20: foto.slice(0, 20),
            ultimos20: foto.slice(-20),
          });
        });

        const mapped = coleccion.map((item) => ({
          id: item.HotProdCod,
          // ✅ El dialog de detalle necesita este campo tal cual (hotprodcod)
          // para poder pedir Actividad_Detalle a la API.
          hotprodcod: item.HotProdCod,
          activityName: item.HotProdNomLargo,
          description: stripHtml(item.HotProdItinerario),
          // ✅ Foto real de la actividad (base64) si la API la trae. Si no
          // viene (o el string es inválido), queda en null y en el render
          // mostramos un placeholder en vez de una imagen genérica.
          image: buildImageSrc(item.HotProdFoto),
          conditions: '',
          price: null,
          currency: '',
        }));

        if (isMounted) setActivitiesList(mapped);
      } catch (err) {
        console.error('Error al cargar actividades:', err);
        if (isMounted) setErrorMsg('No se pudieron cargar las actividades disponibles.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchActivities();
    return () => {
      isMounted = false;
    };
  }, [hotCod]);

  const handleClickOpen = (activity) => {
    setSelectedActivity(activity);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Typography variant="body1" color="error" sx={{ py: 2 }}>
        {errorMsg}
      </Typography>
    );
  }

  if (activitiesList.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
        No hay actividades disponibles para este hotel.
      </Typography>
    );
  }

  return (
    <div>
      <Grid container spacing={3}>
        {
          activitiesList.map((activity) => {
            const showPlaceholder = !activity.image || imageLoadErrors[activity.id];

            return (
            <Grid item xs={12} md={6} key={activity.id}>
              <Card>
                {showPlaceholder ? (
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      bgcolor: 'grey.200',
                      color: 'grey.500',
                    }}
                  >
                    <ImageNotSupportedOutlinedIcon sx={{ fontSize: 48 }} />
                    <Typography variant="caption" color="inherit">
                      Sin imagen disponible
                    </Typography>
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    height="200"
                    image={activity.image}
                    alt={activity.activityName}
                    sx={{ objectFit: 'cover' }}
                    onLoad={(e) => {
                      // 🐞 DEBUG: para confirmar si se ve "pixelada" porque la
                      // API manda una foto de baja resolución y el navegador la
                      // estira para llenar la tarjeta. Si "real" es bastante más
                      // chico que "mostrada", el problema es la foto de origen,
                      // no el código de acá.
                      console.log('[Activities] tamaño de imagen', activity.activityName, {
                        real: `${e.target.naturalWidth}x${e.target.naturalHeight}`,
                        mostrada: `${e.target.clientWidth}x${e.target.clientHeight}`,
                      });
                    }}
                    onError={() => {
                      // Si el base64 vino corrupto/inválido, mostramos el placeholder.
                      handleImageError(activity.id);
                    }}
                  />
                )}
                <CardContent>
                  <Typography variant="h5" component="div">
                    {activity.activityName}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    component="div"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {truncate(activity.description)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    {/* ✅ Un único botón por actividad: abre el mismo diálogo que antes
                        mostraba "Más información". Ahí es donde ahora se elige día/hora
                        y se ve el precio, y desde ahí se agrega la actividad. */}
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={() => handleClickOpen(activity)}
                    >
                      Consultar actividad
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            );
          })
        }
      </Grid>
      <DialogMoreInfoActivity
        open={dialogOpen}
        handleClose={handleClose}
        activity={selectedActivity}
        // ✅ Le pasamos el código de hotel de ADMINTOUR (no el del motor):
        // Actividad_Detalle también necesita ese código.
        hotcod={hotCodAdmintour}
      />
    </div>
  );
}

export default Activities;